import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Building2, ArrowLeft, Users, BarChart3, Settings,
  ToggleLeft, Loader2, Save, Check, X
} from 'lucide-react';
import { toast } from 'sonner';

const TenantDetailPage = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const loadData = async () => {
    try {
      const [tenantRes, featuresRes] = await Promise.all([
        api.get(`/platform/tenants/${tenantId}`),
        api.get(`/platform/tenants/${tenantId}/features`)
      ]);
      setTenant(tenantRes.data);
      setFeatures(featuresRes.data);
      setEditData({
        name: tenantRes.data.name,
        tin: tenantRes.data.tin || '',
        address: tenantRes.data.address || '',
        phone: tenantRes.data.phone || '',
        email: tenantRes.data.email || '',
        is_active: tenantRes.data.is_active,
      });
    } catch (error) {
      toast.error('Failed to load tenant details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [tenantId, api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/platform/tenants/${tenantId}`, editData);
      toast.success('Tenant updated');
      setEditMode(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleFeatureToggle = async (featureKey, isEnabled) => {
    try {
      await api.put(`/platform/tenants/${tenantId}/features`, {
        feature_key: featureKey,
        is_enabled: isEnabled,
      });
      toast.success(`Feature ${isEnabled ? 'enabled' : 'disabled'}`);
      // Update local state
      setFeatures(prev => prev.map(f => 
        f.feature_key === featureKey ? { ...f, is_enabled: isEnabled } : f
      ));
    } catch (error) {
      toast.error('Failed to update feature');
    }
  };

  // Format feature key for display
  const formatFeatureName = (key) => {
    return key
      .replace('FEATURE_', '')
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Tenant not found</p>
        <Button onClick={() => navigate('/platform/tenants')} className="mt-4">
          Back to Tenants
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="tenant-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/platform/tenants')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">{tenant.name}</h1>
            <Badge variant={tenant.is_active ? 'default' : 'destructive'}>
              {tenant.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <p className="text-muted-foreground font-mono">{tenant.slug}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-lg bg-primary/10">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Users</p>
              <p className="text-2xl font-bold">{tenant.metrics?.active_users || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-lg bg-green-500/10">
              <BarChart3 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Sales</p>
              <p className="text-2xl font-bold">{tenant.metrics?.sales_count || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <ToggleLeft className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Features Enabled</p>
              <p className="text-2xl font-bold">
                {features.filter(f => f.is_enabled).length} / {features.length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details" className="gap-2">
            <Settings className="w-4 h-4" />
            Details
          </TabsTrigger>
          <TabsTrigger value="features" className="gap-2">
            <ToggleLeft className="w-4 h-4" />
            Feature Flags
          </TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Organization Details</CardTitle>
                <CardDescription>Basic information about this tenant</CardDescription>
              </div>
              {!editMode ? (
                <Button variant="outline" onClick={() => setEditMode(true)}>
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setEditMode(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="save-tenant-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Business Name</Label>
                  {editMode ? (
                    <Input
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      className="mt-1"
                    />
                  ) : (
                    <p className="mt-1 font-medium">{tenant.name}</p>
                  )}
                </div>
                <div>
                  <Label>Status</Label>
                  {editMode ? (
                    <div className="flex items-center gap-2 mt-2">
                      <Switch
                        checked={editData.is_active}
                        onCheckedChange={(checked) => setEditData({ ...editData, is_active: checked })}
                      />
                      <span>{editData.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  ) : (
                    <Badge className="mt-1" variant={tenant.is_active ? 'default' : 'destructive'}>
                      {tenant.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  )}
                </div>
                <div>
                  <Label>Email</Label>
                  {editMode ? (
                    <Input
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      className="mt-1"
                    />
                  ) : (
                    <p className="mt-1">{tenant.email || '-'}</p>
                  )}
                </div>
                <div>
                  <Label>Phone</Label>
                  {editMode ? (
                    <Input
                      value={editData.phone}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      className="mt-1"
                    />
                  ) : (
                    <p className="mt-1">{tenant.phone || '-'}</p>
                  )}
                </div>
                <div>
                  <Label>TIN</Label>
                  {editMode ? (
                    <Input
                      value={editData.tin}
                      onChange={(e) => setEditData({ ...editData, tin: e.target.value })}
                      className="mt-1"
                    />
                  ) : (
                    <p className="mt-1 font-mono">{tenant.tin || '-'}</p>
                  )}
                </div>
                <div>
                  <Label>VAT Mode</Label>
                  <p className="mt-1">
                    <Badge variant="outline">
                      {tenant.vat_mode === 'vat_registered' ? 'VAT Registered' : 'Non-VAT'}
                    </Badge>
                  </p>
                </div>
                <div className="md:col-span-2">
                  <Label>Address</Label>
                  {editMode ? (
                    <Input
                      value={editData.address}
                      onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                      className="mt-1"
                    />
                  ) : (
                    <p className="mt-1">{tenant.address || '-'}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feature Flags Tab */}
        <TabsContent value="features">
          <Card>
            <CardHeader>
              <CardTitle>Feature Flags</CardTitle>
              <CardDescription>
                Enable or disable features for this tenant. Changes take effect immediately.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {features.map(feature => (
                    <div 
                      key={feature.feature_key}
                      className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                      data-testid={`feature-${feature.feature_key}`}
                    >
                      <div>
                        <div className="font-medium">{formatFeatureName(feature.feature_key)}</div>
                        <div className="text-sm text-muted-foreground font-mono">
                          {feature.feature_key}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={feature.is_enabled ? 'default' : 'secondary'}>
                          {feature.is_enabled ? (
                            <><Check className="w-3 h-3 mr-1" /> Enabled</>
                          ) : (
                            <><X className="w-3 h-3 mr-1" /> Disabled</>
                          )}
                        </Badge>
                        <Switch
                          checked={feature.is_enabled}
                          onCheckedChange={(checked) => handleFeatureToggle(feature.feature_key, checked)}
                          data-testid={`toggle-${feature.feature_key}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TenantDetailPage;
